import {gql} from '@apollo/client';
import React, {createContext, useContext, useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';

import type {MCPEnabledServersQuery} from './__generated/MCPEnabledServersQuery';

export const MCP_ENABLED_SERVERS_QUERY = gql`
  query MCPEnabledServersQuery @skipAudit @engComponent(name: LLM) {
    mcpServerConfigs {
      id
      name
      authType
      iconUrl
    }
    mcpServerUserConfigs {
      id
      mcpServerConfig {
        id
        name
        iconUrl
      }
    }
  }
`;

export interface EnabledServer {
  id: string;
  name: string;
  iconUrl: string | null;
}

export interface DisabledServer {
  id: string;
  name: string;
  authType: string;
  iconUrl: string | null;
}

export interface MCPEnabledServersContextValue {
  isLoading: boolean;
  enabledServers: Array<EnabledServer>;
  disabledServers: Array<DisabledServer>;
  enabledServerIds: Array<string>;
  hasTenantMcpConfigs: boolean;
  refetch: () => void;
}

const MCPEnabledServersContext = createContext<MCPEnabledServersContextValue | null>(null);

interface MCPEnabledServersProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that fetches the current user's connected MCP servers.
 *
 * All unarchived McpServerUserConfig entries represent connected servers.
 * This is separate from MCPToolMappingContext (which handles display name resolution)
 * because the concerns are different: this context tracks which servers are connected
 * for the user, used for sending server IDs with chat messages and (eventually) for
 * per-chat server selection UI.
 */
export function MCPEnabledServersProvider({children}: MCPEnabledServersProviderProps): JSX.Element {
  const queryResult = useBenchlingQuery<MCPEnabledServersQuery>(MCP_ENABLED_SERVERS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const isLoading = queryResult.type === QueryResultType.PENDING;

  const queryData = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : undefined;

  const {enabledServers, disabledServers, enabledServerIds, hasTenantMcpConfigs} = useMemo(() => {
    if (!queryData) {
      return {
        enabledServers: [] as Array<EnabledServer>,
        disabledServers: [] as Array<DisabledServer>,
        enabledServerIds: [] as Array<string>,
        hasTenantMcpConfigs: false,
      };
    }
    const servers = queryData.mcpServerUserConfigs.map((config) => ({
      id: config.mcpServerConfig.id,
      name: config.mcpServerConfig.name,
      iconUrl: config.mcpServerConfig.iconUrl,
    }));
    const enabledServerIdSet = new Set(servers.map((s) => s.id));
    const configsWithoutUserConnection = queryData.mcpServerConfigs.filter(
      (config) => !enabledServerIdSet.has(config.id)
    );

    return {
      enabledServers: servers,
      disabledServers: configsWithoutUserConnection,
      enabledServerIds: servers.map((s) => s.id),
      hasTenantMcpConfigs: queryData.mcpServerConfigs.length > 0,
    };
  }, [queryData]);

  const contextValue = useMemo(
    (): MCPEnabledServersContextValue => ({
      isLoading,
      enabledServers,
      disabledServers,
      enabledServerIds,
      hasTenantMcpConfigs,
      refetch: queryResult.refetch,
    }),
    [isLoading, enabledServers, disabledServers, enabledServerIds, hasTenantMcpConfigs, queryResult.refetch]
  );

  return (
    <MCPEnabledServersContext.Provider value={contextValue}>{children}</MCPEnabledServersContext.Provider>
  );
}

/**
 * Hook to access the list of MCP servers the current user has connected.
 * Returns null if used outside of MCPEnabledServersProvider.
 */
export function useMCPEnabledServersOptional(): MCPEnabledServersContextValue | null {
  return useContext(MCPEnabledServersContext);
}
